import React from "react";

import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import MuiSwitch, { SwitchProps as MuiSwitchProps } from "@mui/material/Switch";
import Typography from "@mui/material/Typography";

import { lightOrDark } from "@app/embedded/theme/utils";

export interface SwitchProps extends MuiSwitchProps {
  checkedColor?: React.CSSProperties["color"];
  uncheckedColor?: React.CSSProperties["color"];
  checkedThumbColor?: React.CSSProperties["color"];
  uncheckedThumbColor?: React.CSSProperties["color"];
  disabledCheckedColor?: React.CSSProperties["color"];
  disabledUncheckedColor?: React.CSSProperties["color"];
  disabledUncheckedThumbColor?: React.CSSProperties["color"];
  switchSize?: "default" | "large" | "small";
  withStatusLabel?: boolean;
  noMargin?: boolean;
}

// custom props that we don't want to pass to the component's render
const CUSTOM_PROPS: PropertyKey[] = [
  "checkedColor",
  "uncheckedColor",
  "checkedThumbColor",
  "uncheckedThumbColor",
  "disabledCheckedColor",
  "disabledUncheckedColor",
  "disabledUncheckedThumbColor",
  "switchSize",
];

const SIZE_STYLES = {
  default: {
    base: {
      width: "40px",
      height: "22px",
    },
    checked: {
      transform: "translateX(18px)",
    },
    thumb: {
      width: "18px",
      height: "18px",
    },
  },
  small: {
    base: {
      width: "32px",
      height: "18px",
    },
    checked: {
      transform: "translateX(14px)",
    },
    thumb: {
      width: "14px",
      height: "14px",
    },
  },
  large: {
    base: {
      width: "60px",
      height: "34px",
    },
    checked: {
      transform: "translateX(26px)",
    },
    thumb: {
      width: "30px",
      height: "30px",
    },
  },
};

const Switch = styled(
  ({ withStatusLabel, noMargin: _, ...props }: SwitchProps) => {
    return withStatusLabel ? (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography
          variant="bodyPrimaryReg"
          sx={{ color: "shades.neutral.500" }}
        >
          {props.checked ? "On" : "Off"}
        </Typography>
        <MuiSwitch disableRipple {...props} />
      </Stack>
    ) : (
      <MuiSwitch disableRipple {...props} />
    );
  },
  {
    shouldForwardProp: (prop) => !CUSTOM_PROPS.includes(prop),
  }
)(({
  theme,
  checkedThumbColor,
  uncheckedThumbColor,
  checkedColor,
  uncheckedColor,
  disabledCheckedColor,
  disabledUncheckedColor,
  disabledUncheckedThumbColor,
  switchSize,
  icon,
  noMargin,
}) => {
  const checkedBackgroundColor =
    checkedColor ?? theme.palette.shades.meadow[500];
  const uncheckedBackgroundColor =
    uncheckedColor ?? theme.palette.shades.neutral[200];
  const checkedThumbBackgroundColor =
    checkedThumbColor ??
    lightOrDark("#fff", theme.palette.shades.neutral[900])(theme);
  const uncheckedThumbBackgroundColor =
    uncheckedThumbColor ??
    lightOrDark("#fff", theme.palette.shades.neutral[900])(theme);
  const disabledCheckedBackgroundColor =
    disabledCheckedColor ?? theme.palette.shades.meadow[300];
  const disabledUncheckedBackgroundColor =
    disabledUncheckedColor ?? theme.palette.shades.neutral[100];
  const disabledUncheckedThumbBackgroundColor =
    disabledUncheckedThumbColor ?? "#fff";
  const sizeStyles = SIZE_STYLES[switchSize ?? "default"];

  return {
    ...sizeStyles.base,
    padding: 0,
    margin: noMargin ? 0 : "6px 9px",
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: "2px",
      transitionDuration: "200ms",
      width: icon ? "18px" : undefined,
      height: icon ? "18px" : undefined,
      "&:hover": {
        backgroundColor: uncheckedThumbBackgroundColor,
        border: `2px solid ${uncheckedBackgroundColor}`,
        margin: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        backgroundColor: disabledUncheckedBackgroundColor,
        opacity: 1,
      },
      "&.Mui-disabled .MuiSwitch-thumb": {
        backgroundColor: disabledUncheckedThumbBackgroundColor,
      },
      "&.Mui-checked": {
        ...sizeStyles.checked,
        color: "#fff",
        backgroundColor: checkedThumbBackgroundColor,
        "&:hover": {
          backgroundColor: checkedThumbBackgroundColor,
          border: `2px solid ${checkedBackgroundColor}`,
          margin: 0,
        },
        ".MuiSwitch-thumb": {
          backgroundColor: checkedThumbBackgroundColor,
          boxShadow:
            "0px 16px 34px -6px rgba(0, 0, 0, 0.18), 0px 0px 1px rgba(0, 0, 0, 0.04)",
        },
        "& + .MuiSwitch-track": {
          backgroundColor: checkedBackgroundColor,
          opacity: 1,
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          backgroundColor: disabledCheckedBackgroundColor,
        },
      },
      "& svg": {
        width: "14px",
        height: "14px",
      },
    },
    "& .MuiSwitch-thumb": {
      ...sizeStyles.thumb,
      backgroundColor: uncheckedThumbBackgroundColor,
      boxSizing: "border-box",
      boxShadow:
        "0px 16px 34px -6px rgba(0, 0, 0, 0.18), 0px 0px 1px rgba(0, 0, 0, 0.04)",
    },
    "& .MuiSwitch-track": {
      borderRadius: "20px",
      backgroundColor: uncheckedBackgroundColor,
      transition: theme.transitions.create(["background-color"], {
        duration: 200,
      }),
      boxShadow: "inset 0px 4px 4px rgba(0, 0, 0, 0.1)",
      opacity: 1,
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      backgroundColor: disabledUncheckedBackgroundColor,
      opacity: 1,
    },
  };
});

export default Switch;
